import React, { useState, useEffect, useCallback } from 'react';
import { useOperatorMode } from '../hooks/useOperatorMode';
import { usePushSubscription } from '../hooks/usePushSubscription';

const DISMISS_KEY = 'operator.push-consent.dismissed';
const FIRST_LOGIN_KEY = 'operator.push-consent.eligible-after';
const DELAY_MS = 8000;

interface PushConsentBannerProps {
  onVisibilityChange?: (visible: boolean) => void;
}

export function PushConsentBanner({ onVisibilityChange }: PushConsentBannerProps = {}) {
  const { isStandalone, isMobile } = useOperatorMode();
  const { supported, permission, subscribed, loading, error, subscribe } = usePushSubscription();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!supported) return;
    if (subscribed) return;
    if (permission === 'denied') return;
    if (!(isStandalone || isMobile)) return;

    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
    } catch { /* */ }

    let eligibleAfter: number;
    try {
      const stored = localStorage.getItem(FIRST_LOGIN_KEY);
      if (stored) {
        eligibleAfter = parseInt(stored, 10);
      } else {
        eligibleAfter = Date.now() + DELAY_MS;
        localStorage.setItem(FIRST_LOGIN_KEY, String(eligibleAfter));
      }
    } catch {
      eligibleAfter = Date.now() + DELAY_MS;
    }

    const remaining = Math.max(0, eligibleAfter - Date.now());
    const timer = setTimeout(() => setVisible(true), remaining);
    return () => clearTimeout(timer);
  }, [supported, subscribed, permission, isStandalone, isMobile]);

  useEffect(() => {
    if (subscribed) setVisible(false);
  }, [subscribed]);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ }
  }, []);

  const handleEnable = useCallback(async () => {
    await subscribe();
  }, [subscribe]);

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Push notification opt-in"
      aria-live="polite"
      data-testid="push-consent-banner"
      style={{
        position: 'fixed',
        bottom: 'calc(var(--op-nav-height, 72px) + env(safe-area-inset-bottom) + 12px)',
        left: 12,
        right: 12,
        zIndex: 150,
        background: 'var(--card-bg, #1a1a2e)',
        border: '1px solid var(--card-border, rgba(255,255,255,0.1))',
        borderRadius: 16,
        padding: 20,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        animation: 'sentinel-fade-in-up 250ms ease-out both',
      }}
    >
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">🔔</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #fff)', marginBottom: 4 }}>
            Get instant alerts
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary, rgba(255,255,255,0.6))', lineHeight: 1.4 }}>
            Know immediately when your sentinels need attention. Push notifications deliver critical alerts even when the app is closed.
          </div>
        </div>
      </div>

      {error && (
        <div
          data-testid="push-consent-error"
          style={{
            fontSize: 13,
            color: '#ff6b6b',
            marginBottom: 10,
            padding: '6px 10px',
            background: 'rgba(255,107,107,0.1)',
            borderRadius: 8,
            lineHeight: 1.4,
          }}
          role="alert"
        >
          {permission === 'denied'
            ? 'Notifications blocked. Enable them in your browser settings.'
            : 'Something went wrong. Use the button below to try again.'}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleEnable}
          disabled={loading || permission === 'denied'}
          data-testid="push-consent-enable"
          style={{
            flex: 1,
            padding: '12px 0',
            borderRadius: 10,
            border: 'none',
            background: 'var(--main-accent, #6C4CFF)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            minHeight: 44,
          }}
        >
          {loading ? 'Enabling...' : error ? 'Try Again' : 'Enable Notifications'}
        </button>
        <button
          onClick={dismiss}
          data-testid="push-consent-dismiss"
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            border: '1px solid var(--card-border, rgba(255,255,255,0.12))',
            background: 'none',
            color: 'var(--text-secondary, rgba(255,255,255,0.5))',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
